import { setProp } from 'libs/utils';
import styled from 'styled-components';
import { device, Breakpoints } from 'GlobalStyles';

interface IGrid {
  flexDirection?: string;
  isNoWrap?: boolean;
  display?: string;
  isInline?: boolean;
  position?: string;
  right?: string;
  left?: string;
  bottom?: string;
  top?: string;
  align?: string;
  justify?: string;
  gap?: string;
  padding?: string;
  margin?: string;
  marginTop?: string;
  border?: string;
  borderRadius?: string;
  backgroundColor?: string;
  overflow?: string;
  cursor?: string;
  fontWeight?: any;
  backgroundSize?: any;
  ref?: any;
  width?: string;
  minWidth?: string;
  maxWidth?: string;
  height?: string;
  minHeight?: string;
  maxHeight?: string;
  image?: string;
  imagePosition?: string;
  imageSize?: string;
}

interface IGridR extends IGrid {
  xs?: any;
  sm?: any;
  md?: any;
  lg?: any;
  xl?: any;
  xxl?: any;
}

const createGridResponsive = (props: IGrid, type: Breakpoints) => {
  if (!props) return '';
  const {
    position,
    right,
    left,
    bottom,
    top,
    align,
    justify,
    gap,
    display,
    isInline,
    isNoWrap,
    padding,
    margin,
    marginTop,
    border,
    borderRadius,
    backgroundColor,
    width,
    maxWidth,
    minWidth,
    height,
    maxHeight,
    minHeight,
  } = props;
  return `
    @media screen and ${device[type]} {
      ${setProp('padding', padding)}
      ${setProp('margin', margin)}
      ${setProp('margin-top', marginTop)}
      ${setProp('gap', gap)}
      ${setProp('align-items', align)}
      ${setProp('align-content', align)}
      ${setProp('justify-content', justify)}
      ${setProp('position', position)}
      ${setProp('right', right)}
      ${setProp('left', left)}
      ${setProp('bottom', bottom)}
      ${setProp('top', top)}
      ${setProp('border', border)}
      ${setProp('border-radius', borderRadius)}
      ${setProp('background-color', backgroundColor)}
      ${setProp('height', height)}
      ${setProp('min-height', minHeight)}
      ${setProp('max-height', maxHeight)}
      ${setProp('width', width)}
      ${setProp('min-width', minWidth)}
      ${setProp('max-width', maxWidth)}
      ${setProp('display', display || (isInline && 'inline-flex'))}
      ${setProp('flex-wrap', isNoWrap ? 'nowrap' : 'wrap')}
    }
  `;
};

export const Grid = styled.div`
  ${({
    isNoWrap = false,
    display,
    isInline = false,
    position,
    right,
    left,
    bottom,
    top,
    flexDirection,
    align,
    justify,
    gap,
    padding,
    margin,
    marginTop,
    border,
    borderRadius,
    backgroundColor,
    width,
    maxWidth,
    minWidth,
    height,
    maxHeight,
    minHeight,
    overflow,
    cursor,
    fontWeight,
    backgroundSize,
    image,
    imagePosition = 'center',
    imageSize = 'cover',
    xs,
    sm,
    md,
    lg,
    xl,
    xxl,
  }: IGridR) => {
    return `
      flex-wrap: ${isNoWrap ? 'nowrap' : 'wrap'};
      display: ${display || (isInline ? 'inline-flex' : 'flex')};
      ${setProp('padding', padding)}
      ${setProp('margin', margin)}
      ${setProp('margin-top', marginTop)}
      ${setProp('gap', gap)}
      ${setProp('flex-direction', flexDirection)}
      ${setProp('align-items', align)}
      ${setProp('align-content', align)}
      ${setProp('justify-content', justify)}
      ${setProp('position', position)}
      ${setProp('right', right)}
      ${setProp('left', left)}
      ${setProp('bottom', bottom)}
      ${setProp('top', top)}
      ${setProp('border', border)}
      ${setProp('border-radius', borderRadius)}
      ${setProp('background-color', backgroundColor)}
      ${setProp('height', height)}
      ${setProp('min-height', minHeight)}
      ${setProp('max-height', maxHeight)}
      ${setProp('min-width', minWidth)}
      ${setProp('max-width', maxWidth)}
      ${setProp('cursor', cursor)}
      ${setProp('font-weight', fontWeight)}
      ${setProp('backgoundSize', backgroundSize)}
      ${setProp('overflow', overflow)}
      width: ${width ? width : isInline ? 'auto' : '100%'};
      ${
        image
          ? `
          background-image: url(${image});
          background-position: ${imagePosition};
          background-repeat: no-repeat;
          background-size: ${imageSize};
        `
          : ''
      }
      ${createGridResponsive(xxl, 'xxl')}
      ${createGridResponsive(xl, 'xl')}
      ${createGridResponsive(lg, 'lg')}
      ${createGridResponsive(md, 'md')}
      ${createGridResponsive(sm, 'sm')}
      ${createGridResponsive(xs, 'xs')}
    `;
  }}
`;

interface IText {
  position?: string;
  padding?: string;
  margin?: string;
  marginTop?: string;
  backgroundColor?: string;
  width?: string;
  height?: string;
  textDecoration?: string;
  overflow?: string;
  ellipsis?: number;
  color?: string;
  size?: string;
  lineHeight?: number;
  bold?: boolean;
  align?: string;
  disabled?: boolean;
}
interface ITextR extends IText {
  xs?: any;
  sm?: any;
  md?: any;
  lg?: any;
  xl?: any;
  xxl?: any;
}

const createTextResponsive = (props: IText, type: Breakpoints) => {
  if (!props) return '';
  const {
    position,
    textDecoration,
    padding,
    margin,
    marginTop,
    backgroundColor,
    width,
    height,
    overflow,
    ellipsis,
    size = '1rem',
    color,
    lineHeight,
    align,
  } = props;
  return `
    @media screen and ${device[type]} {
      ${setProp('color', color)}
      ${setProp('position', position)}
      ${setProp('padding', padding)}
      ${setProp('margin', margin)}
      ${setProp('margin-top', marginTop)}
      ${setProp('width', width)}
      ${setProp('height', height)}
      ${setProp('text-decoration', textDecoration)}
      ${setProp('background-color', backgroundColor)}
      ${setProp('line-height', lineHeight)}
      ${setProp('font-size', size)}
      ${setProp('text-align', align)}
      ${
        ellipsis
          ? `
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        -webkit-line-clamp: ${ellipsis};
        -webkit-box-orient: vertical;
      `
          : setProp('overflow', overflow)
      }
    }
  `;
};

export const Text = styled.div`
  display: inline-block;
  word-wrap: break-word;
  ${({
    position,
    textDecoration,
    padding,
    margin,
    marginTop,
    backgroundColor,
    width,
    height,
    overflow,
    ellipsis,
    size,
    align,
    disabled = false,
    color = '#14112A',
    lineHeight,
    bold = false,
    xs,
    sm,
    md,
    lg,
    xl,
    xxl,
  }: ITextR) => `
      ${setProp('color', disabled ? `${color}66` : color)}
      ${setProp('position', position)}
      ${setProp('padding', padding)}
      ${setProp('margin', margin)}
      ${setProp('margin-top', marginTop)}
      ${setProp('width', width)}
      ${setProp('height', height)}
      ${setProp('text-decoration', textDecoration)}
      ${setProp('background-color', backgroundColor)}
      ${setProp('line-height', lineHeight)}
      ${setProp('font-size', size)}
      ${setProp('text-align', align)}
      ${bold ? 'font-weight: 700;' : ''}
      ${
        ellipsis
          ? `
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        -webkit-line-clamp: ${ellipsis};
        -webkit-box-orient: vertical;
      `
          : setProp('overflow', overflow)
      }
      ${createTextResponsive(xxl, 'xxl')}
      ${createTextResponsive(xl, 'xl')}
      ${createTextResponsive(lg, 'lg')}
      ${createTextResponsive(md, 'md')}
      ${createTextResponsive(sm, 'sm')}
      ${createTextResponsive(xs, 'xs')}
    `}
`;
